"use client";

import * as Dialog from "@radix-ui/react-dialog";
import { ArrowUpRight, Menu, Phone, X, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { buttonVariants } from "@/components/ui/button";
import { navigation } from "@/config/navigation";
import { site } from "@/config/site";
import { cn } from "@/lib/utils/cn";

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = navigation
      .map((item) => document.querySelector<HTMLElement>(item.href))
      .filter((section): section is HTMLElement => Boolean(section));

    if (sections.length === 0 || typeof IntersectionObserver === "undefined") return;

    const visible = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visible.set(`#${entry.target.id}`, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        let bestId = "";
        let bestRatio = 0;
        for (const [id, ratio] of visible) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        }
        if (bestId) setActiveId(bestId);
      },
      { rootMargin: "-72px 0px -55% 0px", threshold: [0.1, 0.3, 0.6] },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 border-b border-line bg-[rgba(247,244,238,.88)] backdrop-blur transition-shadow duration-300",
        scrolled && "shadow-soft",
      )}
    >
      <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between gap-4 px-6">
        <a
          href="/"
          className="flex items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:shadow-focus"
        >
          <span className="grid size-10 shrink-0 place-items-center rounded-none bg-primary text-navy">
            <Zap className="size-5" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-display text-[17px] font-bold text-ink">{site.name}</span>
            <span className="hidden text-[11px] font-normal text-muted sm:block">
              {site.shortTagline}
            </span>
          </span>
        </a>

        <nav aria-label="Hlavní navigace" className="hidden md:block">
          <ul className="flex items-center gap-1 lg:gap-2">
            {navigation.map((item) => (
              <li key={item.href}>
                <a
                  href={item.href}
                  aria-current={activeId === item.href ? "true" : undefined}
                  className={cn(
                    "rounded-none px-3 py-2 text-[13px] uppercase tracking-[0.14em] transition-colors duration-300 lg:text-[12px]",
                    activeId === item.href
                      ? "font-semibold text-primary-deep underline decoration-primary/60 decoration-1 underline-offset-[10px]"
                      : "text-neutral-600 hover:text-primary-deep",
                  )}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={site.phoneHref}
            className="hidden items-center gap-2 rounded-none px-3 py-2 text-[13px] font-medium tracking-[0.06em] text-ink transition-colors duration-300 hover:text-primary-deep lg:inline-flex"
          >
            <Phone className="size-4 text-primary" aria-hidden="true" />
            {site.phone}
          </a>

          <a
            href="#kontakt"
            className={cn(buttonVariants({ size: "sm" }), "hidden h-11 px-5 md:inline-flex")}
          >
            Poptat konzultaci
            <ArrowUpRight className="size-4" aria-hidden="true" />
          </a>

          <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
            <Dialog.Trigger
              aria-label="Otevřít menu"
              className="grid size-11 place-items-center rounded-none bg-white text-ink ring-1 ring-line transition-colors duration-200 hover:bg-surface-alt focus-visible:outline-none focus-visible:shadow-focus md:hidden"
            >
              <Menu className="size-5" aria-hidden="true" />
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm" />
              <Dialog.Content className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col gap-6 overflow-y-auto bg-base p-6 shadow-hover focus:outline-none">
                <div className="flex items-center justify-between">
                  <Dialog.Title className="font-display text-lg font-bold text-ink">
                    Menu
                  </Dialog.Title>
                  <Dialog.Close
                    aria-label="Zavřít menu"
                    className="grid size-11 place-items-center rounded-none bg-white text-ink ring-1 ring-line transition-colors duration-200 hover:bg-surface-alt focus-visible:outline-none focus-visible:shadow-focus"
                  >
                    <X className="size-5" aria-hidden="true" />
                  </Dialog.Close>
                </div>

                <nav aria-label="Mobilní navigace">
                  <ul className="flex flex-col gap-1">
                    {navigation.map((item) => (
                      <li key={item.href}>
                        <Dialog.Close asChild>
                          <a
                            href={item.href}
                            className="block rounded-2xl px-4 py-3 font-display text-lg font-semibold text-ink transition-colors duration-200 hover:bg-surface-alt focus-visible:outline-none focus-visible:shadow-focus"
                          >
                            {item.label}
                          </a>
                        </Dialog.Close>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="mt-auto flex flex-col gap-3">
                  <a
                    href={site.phoneHref}
                    className={cn(buttonVariants({ variant: "outline", size: "lg" }), "w-full")}
                  >
                    <Phone className="size-4 text-primary" aria-hidden="true" />
                    {site.phone}
                  </a>
                  <Dialog.Close asChild>
                    <a href="#kontakt" className={cn(buttonVariants({ size: "lg" }), "w-full")}>
                      Poptat konzultaci
                      <ArrowUpRight className="size-4" aria-hidden="true" />
                    </a>
                  </Dialog.Close>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      </div>
    </header>
  );
}
